/**
 * Exhibition context helpers.
 *
 * Everything related to the currently selected exhibition lives here:
 * - localStorage keys
 * - Read / write / clear helpers
 * - Building the X-Exhibition-ID header object consumed by every API service
 */

export const EXHIBITION_ID_KEY   = "activeExhibitionId";
export const EXHIBITION_DATA_KEY = "activeExhibitionData";

// ── Read ────────────────────────────────────────────────────────────────────

export function getActiveExhibitionId(): string | null {
  return localStorage.getItem(EXHIBITION_ID_KEY);
}

export interface StoredExhibition {
  id: number;
  title: string;
  venue: string;
  start_date: string;
  end_date: string;
  status: boolean;
  exhibition_type: string | null;
  description: string | null;
  phone_number: string | null;
  email: string | null;
  is_current: boolean;
}

export function getActiveExhibitionData(): StoredExhibition | null {
  try {
    const raw = localStorage.getItem(EXHIBITION_DATA_KEY);
    return raw ? (JSON.parse(raw) as StoredExhibition) : null;
  } catch {
    return null;
  }
}

// ── Write ───────────────────────────────────────────────────────────────────

export function setActiveExhibition(exhibition: StoredExhibition): void {
  localStorage.setItem(EXHIBITION_ID_KEY, String(exhibition.id));
  localStorage.setItem(EXHIBITION_DATA_KEY, JSON.stringify(exhibition));
  // Emit a custom event so any listening component can re-render synchronously
  window.dispatchEvent(new CustomEvent("exhibitionChanged", { detail: exhibition }));
}

// ── Clear ────────────────────────────────────────────────────────────────────

export function clearActiveExhibition(): void {
  localStorage.removeItem(EXHIBITION_ID_KEY);
  localStorage.removeItem(EXHIBITION_DATA_KEY);
}

// ── Axios header helper ──────────────────────────────────────────────────────

/**
 * Returns an object that can be spread into `config.headers` inside any
 * axios request interceptor to forward the active exhibition to the backend.
 *
 * Usage in a service interceptor:
 *   config.headers = { ...config.headers, ...getExhibitionHeader() };
 */
export function getExhibitionHeader(): Record<string, string> {
  const id = getActiveExhibitionId();
  return id ? { "X-Exhibition-ID": id } : {};
}
